import { useCallback } from 'react'
import { useDialog } from './useDialog'
import type { Id } from '../../../convex/_generated/dataModel'

export type ModalType =
  | 'edit-project' | 'edit-scenario' | 'edit-dataset'
  | 'delete-project' | 'delete-scenario' | 'delete-dataset'
  | 'clone-scenario' | 'clone-dataset'
  | 'bulk-delete' | 'bulk-clone'
  | 'table-import-csv' | 'table-export-csv' | 'table-bulk-edit' | 'table-location-form'

export interface ModalData {
  id?: Id<'projects'> | Id<'scenarios'> | Id<'datasets'>
  name?: string
  type?: 'project' | 'scenario' | 'dataset'
  parentId?: Id<'projects'> | Id<'scenarios'>
  selectedIds?: string[]
  cascadeInfo?: { childCount: number; childType: string; warnings: string[] }
  tableType?: 'vehicles' | 'jobs' | 'locations' | 'routes'
  selectedRows?: any[]
  coordinates?: [number, number]
  address?: string
  location?: any
  locationFormMode?: 'create' | 'edit'
  availableDatasets?: any
  availableTables?: any
}

interface ModalDataWithType extends ModalData {
  modalType: ModalType | null
}

export interface ModalState {
  isOpen: boolean
  type: ModalType | null
  data: ModalData | null
  isLoading: boolean
  error: string | null
}

export interface UseModalStateReturn {
  modalState: ModalState
  openModal: (type: ModalType, data?: ModalData) => void
  closeModal: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  updateModalData: (data: Partial<ModalData>) => void
  isModalOpen: (type?: ModalType) => boolean
  isAnyModalOpen: () => boolean
  getCurrentModalType: () => ModalType | null
  getCurrentModalData: () => ModalData | null
  openEditModal: (entityType: 'project' | 'scenario' | 'dataset', id: Id<any>, name: string) => void
  openDeleteModal: (entityType: 'project' | 'scenario' | 'dataset', id: Id<any>, name: string, cascadeInfo?: ModalData['cascadeInfo']) => void
  openCloneModal: (entityType: 'scenario' | 'dataset', id: Id<any>, name: string, parentId?: Id<any>) => void
  openBulkModal: (operation: 'delete' | 'clone', selectedIds: string[], entityType: 'scenario' | 'dataset') => void
  openTableModal: (modalType: 'table-import-csv' | 'table-export-csv' | 'table-bulk-edit', tableType: 'vehicles' | 'jobs' | 'locations' | 'routes', additionalData?: Partial<ModalData>) => void
  openLocationFormModal: (mode: 'create' | 'edit', coordinates?: [number, number], address?: string, location?: any) => void
}

export const useModalState = (options: { onSuccess?: (type: ModalType, data: ModalData) => void; onError?: (type: ModalType, error: string) => void; closeOnEscape?: boolean } = {}): UseModalStateReturn => {
  const baseDialog = useDialog<ModalDataWithType>({ closeOnEscape: options.closeOnEscape })

  const openModal = useCallback((type: ModalType, data: ModalData = {}) => {
    baseDialog.open({ ...data, modalType: type })
  }, [baseDialog])

  const closeModal = useCallback(() => baseDialog.close(), [baseDialog])
  const setLoading = useCallback((loading: boolean) => baseDialog.setLoading(loading), [baseDialog])
  const setError = useCallback((error: string | null) => baseDialog.setError(error), [baseDialog])
  const updateModalData = useCallback((data: Partial<ModalData>) => baseDialog.updateData(data), [baseDialog])

  const modalState: ModalState = {
    isOpen: baseDialog.state.isOpen,
    type: baseDialog.state.data?.modalType || null,
    data: baseDialog.state.data ? { ...baseDialog.state.data, modalType: undefined } as ModalData : null,
    isLoading: baseDialog.state.isLoading,
    error: baseDialog.state.error,
  }

  const isModalOpen = useCallback((type?: ModalType) => type ? baseDialog.state.isOpen && baseDialog.state.data?.modalType === type : baseDialog.state.isOpen, [baseDialog.state])
  const isAnyModalOpen = useCallback(() => baseDialog.state.isOpen, [baseDialog.state])
  const getCurrentModalType = useCallback(() => baseDialog.state.data?.modalType || null, [baseDialog.state])
  const getCurrentModalData = useCallback(() => baseDialog.state.data ? { ...baseDialog.state.data, modalType: undefined } as ModalData : null, [baseDialog.state])

  const openEditModal = useCallback((entityType: 'project' | 'scenario' | 'dataset', id: Id<any>, name: string) => {
    openModal(`edit-${entityType}` as ModalType, { id, name, type: entityType })
  }, [openModal])

  const openDeleteModal = useCallback((entityType: 'project' | 'scenario' | 'dataset', id: Id<any>, name: string, cascadeInfo?: ModalData['cascadeInfo']) => {
    openModal(`delete-${entityType}` as ModalType, { id, name, type: entityType, cascadeInfo })
  }, [openModal])

  const openCloneModal = useCallback((entityType: 'scenario' | 'dataset', id: Id<any>, name: string, parentId?: Id<any>) => {
    openModal(`clone-${entityType}` as ModalType, { id, name, type: entityType, parentId })
  }, [openModal])

  const openBulkModal = useCallback((operation: 'delete' | 'clone', selectedIds: string[], entityType: 'scenario' | 'dataset') => {
    openModal(`bulk-${operation}` as ModalType, { selectedIds, type: entityType })
  }, [openModal])

  const openTableModal = useCallback((modalType: 'table-import-csv' | 'table-export-csv' | 'table-bulk-edit', tableType: 'vehicles' | 'jobs' | 'locations' | 'routes', additionalData?: Partial<ModalData>) => {
    openModal(modalType, { tableType, ...additionalData })
  }, [openModal])

  const openLocationFormModal = useCallback((mode: 'create' | 'edit', coordinates?: [number, number], address?: string, location?: any) => {
    openModal('table-location-form', { locationFormMode: mode, coordinates, address, location })
  }, [openModal])

  return { modalState, openModal, closeModal, setLoading, setError, updateModalData, isModalOpen, isAnyModalOpen, getCurrentModalType, getCurrentModalData, openEditModal, openDeleteModal, openCloneModal, openBulkModal, openTableModal, openLocationFormModal }
}
